import React, {useCallback, useEffect, useState} from 'react';
import type {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';

import WaterDropIcon from '@mui/icons-material/WaterDrop';
import IconButton from '@mui/material/IconButton/IconButton.js';

import type {WbcDiffModel} from '@refinio/one.models/lib/models';
import type {SHA256Hash} from '@refinio/one.core/lib/util/type-checks';

import DefaultAttachmentDetails from '@/root/chat/attachmentViews/defaultView/DefaultAttachmentDetails.js';
import ViewWBCDiff from '@/root/wbc/view/ViewWBCDiff';

/**
 * @param props.getCertificatePopupView callback to get certificate popup view
 * @param props.onError Optional. Default console.error
 * @returns
 */
export default function WBCView(props: {
    hash: SHA256Hash;
    wbcDiffModel: WbcDiffModel;
    getCertificatePopupView: (onClose: () => void) => ReactElement;
    onError?: (e: Error) => void;
}): ReactElement {
    const i18n = useTranslation();
    const [dataObjectId, setDataObjectId] = useState<string | undefined>(undefined);
    const [isObjectViewOpened, setIsObjectViewOpened] = useState<boolean>(false);

    const toggleObjectView = useCallback(() => {
        setIsObjectViewOpened(oldValue => !oldValue);
    }, []);

    useEffect(() => {
        async function loadDataObjectId(): Promise<void> {
            for await (const dataObject of props.wbcDiffModel.observationsIterator()) {
                if (dataObject.dataHash === props.hash) {
                    setDataObjectId(dataObject.id);
                    return;
                }
            }
        }

        if (dataObjectId !== undefined) {
            return;
        }

        loadDataObjectId().catch(props.onError ? props.onError : console.error);
        return props.wbcDiffModel.onUpdated(loadDataObjectId);
    }, [props.wbcDiffModel]);

    if (!dataObjectId) {
        return <></>;
    }

    return (
        <div className="attachment-box hash-attachment">
            <IconButton
                className="attachment-button"
                sx={{'&:hover': {backgroundColor: 'transparent'}, padding: 0}}
                onClick={toggleObjectView}
            >
                <WaterDropIcon className="icon-avatar-wrapper attachment-icon" />
                <div className="text">{i18n.t('journal.eventTypes.wbcDiffTitle')}</div>
            </IconButton>
            <DefaultAttachmentDetails
                getCertificatePopupView={props.getCertificatePopupView}
                onViewObjectRequest={toggleObjectView}
            />
            {isObjectViewOpened && (
                <ViewWBCDiff
                    wbcDiffModel={props.wbcDiffModel}
                    id={dataObjectId}
                    onClose={toggleObjectView}
                />
            )}
        </div>
    );
}
